import React, { useEffect, useState } from "react";
import {
    CircularProgress,
    Drawer,
    Modal,
    ModalDialog,
    ModalClose,
    Divider,
    Chip,
    Tabs,
    TabList,
    Tab,
    TabPanel
} from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import Button from "@mui/joy/Button";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';
import {
    CalendarMonth,
    DeleteForever,
    WarningAmber,
    Cancel
} from "@mui/icons-material";

import ApiService from "../../axios/Bookings-Axios";
import GeneralApiService from "../../axios/Axios";
import DrawerWithStickyFooter from "../dashboard-components/DrawerWithStickyFooter";
import ConfirmDeleteModal from "./ConfirmDeleteModal";

interface Customer {
    full_name?: string;
    street_address?: string;
    postcode?: string;
    phone?: string;
    email?: string;
    notes?: string;
    house_type?: { name: string; id: number };
    franchise_postcode?: {
        franchise?: {
            location?: string;
        };
        service_price_tier?: any;
    };
}

interface Service {
    name: string;
}

interface ServicePrice {
    price: number;
    service: Service;
}

interface Booking {
    id: number;
    customer?: Customer;
    booked_in?: string;
    services?: Service[];
    service_price?: ServicePrice[];
    days_to_complete?: number;
    total_cost?: number;
    readable_services?: string;
    notes?: string;
}

interface RescheduleDrawerProps {
    open: boolean;
    bookingId: number;
    onClose: (refreshData?: boolean) => void;
}

function formatDate(old_date: string | Date) {
    let date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
            hour: 'numeric', minute: 'numeric'
        });
    }
    return 'No Valid Date';
}

const toDateKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export default function RescheduleDrawer({ open, bookingId, onClose }: RescheduleDrawerProps) {
    const [booking, setBooking] = useState<Booking | null>(null);
    const [loadedData, setLoadedData] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showRescheduleConfirm, setShowRescheduleConfirm] = useState(false);
    const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
    const [currentMonthView, setCurrentMonthView] = useState(new Date());
    const [isMonthLoading, setIsMonthLoading] = useState(false);

    // Fetch booking data when drawer opens
    useEffect(() => {
        if (open && bookingId > 0) {
            setLoadedData(false);
            ApiService.getBookingById(bookingId)
                .then(data => {
                    setBooking(data);
                    setSelectedDate(data.booked_in ? new Date(data.booked_in) : null);
                    setCurrentMonthView(data.booked_in ? new Date(data.booked_in) : new Date());
                })
                .catch(err => {
                    console.error("Failed to fetch booking:", err);
                    toast.error("Failed to load booking details");
                    setBooking(null);
                })
                .finally(() => {
                    setLoadedData(true);
                });
        } else {
            setBooking(null);
            setSelectedDate(null);
            setLoadedData(true);
        }
    }, [open, bookingId]);

    // Fetch available dates when month changes
    useEffect(() => {
        const location = booking?.customer?.franchise_postcode?.franchise?.location;
        if (!location || !open) {
            setAvailableDates(new Set());
            return;
        }

        setIsMonthLoading(true);

        const year = currentMonthView.getFullYear();
        const month = currentMonthView.getMonth();
        const startDate = new Date(year, month, 1).toISOString().split('T')[0];
        const endDate = new Date(year, month + 1, 0).toISOString().split('T')[0];

        const excludeBookingId = booking?.id && booking.id > 0 ? booking.id : null;
        GeneralApiService.getFranchiseAvailableDates(
            location,
            startDate,
            endDate,
            booking?.days_to_complete || 1,
            excludeBookingId
        )
            .then(data => {
                setAvailableDates(new Set<string>(data.available_dates));
            })
            .catch(err => {
                console.error("Failed to fetch available dates:", err);
                setAvailableDates(new Set());
            })
            .finally(() => {
                setIsMonthLoading(false);
            });

    }, [booking?.customer?.franchise_postcode?.franchise?.location, booking?.days_to_complete, currentMonthView, open]);

    const isDateAvailable = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const dateString = `${year}-${month}-${day}`;
        return availableDates.has(dateString);
    };

    const handleMonthChange = (date: Date) => {
        setCurrentMonthView(date);
    };

    const handleRescheduleClick = () => {
        if (!booking || !selectedDate) {
            toast.error("Please select a date to reschedule");
            return;
        }
        setShowRescheduleConfirm(true);
    };

    const handleRescheduleConfirm = async () => {
        if (!booking || !selectedDate) return;

        setIsSubmitting(true);
        try {
            await ApiService.rescheduleBooking(booking.id, toDateKey(selectedDate));
            toast.success("Booking rescheduled successfully!");
            setShowRescheduleConfirm(false);
            onClose(true);
        } catch (error) {
            console.error("Error rescheduling booking:", error);
            toast.error("Failed to reschedule booking. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = async () => {
        if (!booking) return;

        setIsSubmitting(true);
        try {
            await ApiService.cancelBooking(booking.id);
            toast.success("Booking cancelled successfully!");
            setShowCancelConfirm(false);
            onClose(true);
        } catch (error) {
            console.error("Error cancelling booking:", error);
            toast.error("Failed to cancel booking. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteCallback = async (doDelete: boolean) => {
        setShowDeleteConfirm(false);
        if (!doDelete || !booking) return;

        setIsSubmitting(true);
        try {
            await ApiService.deleteBookingById(booking.id);
            toast.success("Booking deleted successfully!");
            onClose(true);
        } catch (error) {
            console.error("Error deleting booking:", error);
            toast.error("Failed to delete booking. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeModal = () => {
        onClose();
    };

    const renderFooter = () => {
        if (!loadedData) return <Box sx={{ mr: 'auto' }} />;
        return (
            <>
                {/* Mobile layout - stacked */}
                <Box sx={{ 
                    display: { xs: 'flex', sm: 'none' }, 
                    flexDirection: 'column',
                    gap: 1,
                    width: '100%',
                }}>
                    <Button
                        color="danger"
                        startDecorator={<DeleteForever />}
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isSubmitting || !booking}
                        fullWidth
                    >
                        Delete
                    </Button>
                    <Button
                        color="danger"
                        variant="outlined"
                        startDecorator={<Cancel />}
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={isSubmitting || !booking}
                        fullWidth
                    >
                        Cancel Booking
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleRescheduleClick}
                        loading={isSubmitting}
                        disabled={!selectedDate || isSubmitting || !booking}
                        fullWidth
                    >
                        Confirm Reschedule
                    </Button>
                </Box>
                
                {/* Desktop layout - horizontal */}
                <Box sx={{ 
                    display: { xs: 'none', sm: 'flex' }, 
                    gap: 1,
                    width: '100%',
                    alignItems: 'center',
                }}>
                    <Button
                        sx={{ mr: 1 }}
                        color="danger"
                        startDecorator={<DeleteForever />}
                        onClick={() => setShowDeleteConfirm(true)}
                        disabled={isSubmitting || !booking}
                    >
                        Delete
                    </Button>
                    <Button
                        sx={{ mr: 'auto' }}
                        color="danger"
                        variant="outlined"
                        startDecorator={<Cancel />}
                        onClick={() => setShowCancelConfirm(true)}
                        disabled={isSubmitting || !booking}
                    >
                        Cancel Booking
                    </Button>
                    <Button
                        color="primary"
                        onClick={handleRescheduleClick}
                        loading={isSubmitting}
                        disabled={!selectedDate || isSubmitting || !booking}
                    >
                        Confirm Reschedule
                    </Button>
                </Box>
            </>
        );
    };

    return (
        <>
            {/* Delete Confirmation Modal */}
            <ConfirmDeleteModal
                openState={showDeleteConfirm}
                discardMessage={"Are you sure you want to delete this booking? This action cannot be undone."}
                parentCallback={handleDeleteCallback}
            />

            {/* Cancel Confirmation Modal */}
            <Modal open={showCancelConfirm} onClose={() => setShowCancelConfirm(false)}>
                <ModalDialog
                    variant="outlined"
                    role="alertdialog"
                    sx={{ maxWidth: 400 }}
                >
                    <ModalClose />
                    <Typography
                        level="h4"
                        startDecorator={<WarningAmber color="warning" />}
                    >
                        Cancel Booking?
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-md">
                        Are you sure you want to cancel this booking for <strong>{booking?.customer?.full_name}</strong>?
                    </Typography>
                    <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                        This action will mark the booking as cancelled.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setShowCancelConfirm(false)}
                            disabled={isSubmitting}
                        >
                            Keep Booking
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleCancel}
                            loading={isSubmitting}
                        >
                            Yes, Cancel Booking
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            {/* Reschedule Confirmation Modal */}
            <Modal open={showRescheduleConfirm} onClose={() => setShowRescheduleConfirm(false)}>
                <ModalDialog
                    variant="outlined"
                    role="alertdialog"
                    sx={{ maxWidth: 400 }}
                >
                    <ModalClose />
                    <Typography
                        level="h4"
                        startDecorator={<CalendarMonth color="primary" />}
                    >
                        Confirm Reschedule
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Typography level="body-md">
                        Are you sure you want to reschedule this booking for <strong>{booking?.customer?.full_name}</strong>?
                    </Typography>
                    <Typography level="body-sm" sx={{ mt: 1, color: 'text.secondary' }}>
                        New date: <strong>{selectedDate ? formatDate(selectedDate) : 'N/A'}</strong>
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={() => setShowRescheduleConfirm(false)}
                            disabled={isSubmitting}
                        >
                            Go Back
                        </Button>
                        <Button
                            variant="solid"
                            color="primary"
                            onClick={handleRescheduleConfirm}
                            loading={isSubmitting}
                        >
                            Yes, Reschedule
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>

            <Drawer
                open={open}
                onClose={closeModal}
                anchor="right"
                slotProps={{
                    backdrop: { sx: { backdropFilter: 'none', backgroundColor: 'rgb(108 108 108 / 60%)' } },
                    content: { sx: { 
                        top: 'var(--Header-height)', 
                        height: 'calc(100vh - var(--Header-height))' 
                    } }
                }}
                sx={{ '--Drawer-horizontalSize': { xs: '100vw', sm: '85vw', md: '50vw' } }}
            >
                <DrawerWithStickyFooter footer={renderFooter()}>
                    <ModalClose sx={{ position: 'absolute', top: 8, right: 8, zIndex: 10 }} />
                    <Tabs sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', '& .MuiTabPanel-root': { flex: 1, overflowY: 'auto', minHeight: 0 } }} aria-label="Reschedule Modal Tabs" value={0}>
                        <TabList sx={{ flexShrink: 0 }}>
                            <Tab>Reschedule Booking</Tab>
                        </TabList>
                        <TabPanel value={0}>
                            {!loadedData ? (
                                <Box sx={{ display: 'flex', minHeight: '300px', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                                    <CircularProgress size="lg" />
                                </Box>
                            ) : !booking ? (
                                <Box sx={{ display: 'flex', minHeight: '300px', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography level="body-md">Booking not found</Typography>
                                </Box>
                            ) : (
                                <Box>
                                    {/* Customer Information - Read Only */}
                                    <Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Customer Information</Typography>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                        <Typography>Customer: <Typography fontWeight={500}>{booking.customer?.full_name || 'Unknown'}</Typography></Typography>
                                    </Box>
                                    <Box sx={{ border: '1px solid #CDD7E1', borderRadius: '6px', padding: '10px', mb: '10px' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
                                            <Box sx={{ width: 'calc(50% - 8px)' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Phone</Typography>
                                                <Typography level="body-sm">{booking.customer?.phone || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ width: 'calc(50% - 8px)' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Email</Typography>
                                                <Typography level="body-sm">{booking.customer?.email || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', mt: 1 }}>
                                            <Box sx={{ width: 'calc(50% - 8px)' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Address</Typography>
                                                <Typography level="body-sm">{booking.customer?.street_address || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ width: 'calc(50% - 8px)' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Postcode</Typography>
                                                <Typography level="body-sm">{booking.customer?.postcode || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%', mt: 1 }}>
                                            <Box sx={{ width: 'calc(50% - 8px)' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>House Type</Typography>
                                                <Typography level="body-sm">{booking.customer?.house_type?.name || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ width: 'calc(50% - 8px)' }}>
                                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>Franchise</Typography>
                                                <Typography level="body-sm">{booking.customer?.franchise_postcode?.franchise?.location || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Customer Notes */}
                                    {booking.customer?.notes && (
                                        <Box sx={{ mt: '10px' }}>
                                            <Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Customer Notes</Typography>
                                            <Box sx={{ border: '1px solid #CDD7E1', borderRadius: '6px', padding: '10px', maxHeight: '100px', overflowY: 'auto' }}>
                                                <Typography level="body-sm" sx={{ whiteSpace: 'pre-wrap' }}>{booking.customer.notes}</Typography>
                                            </Box>
                                        </Box>
                                    )}

                                    {/* Booking Details */}
                                    <Box sx={{ mt: '10px' }}>
                                        <Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Booking Details</Typography>
                                        <Box sx={{ border: '1px solid #CDD7E1', borderRadius: '6px', padding: '10px' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography level="body-sm">Services:</Typography>
                                                <Typography level="body-sm" fontWeight={500}>{booking.readable_services || 'N/A'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography level="body-sm">Total Price:</Typography>
                                                <Typography level="title-sm" fontWeight={600}>£{booking.total_cost?.toFixed(2) || '0.00'}</Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography level="body-sm">Current Date:</Typography>
                                                <Chip size="sm" variant="soft" color="warning" startDecorator={<CalendarMonth sx={{ fontSize: 14 }} />}>
                                                    {formatDate(booking.booked_in || '')}
                                                </Chip>
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Booking Time - Same as BookingsDrawer */}
                                    <Box sx={{ mt: '10px' }}>
                                        <Typography variant="soft" sx={{ mb: '5px', width: '100%' }}>Booking Time</Typography>

                                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                                            <Box sx={{ minWidth: { sm: '250px' } }}>
                                                <Box sx={{ position: 'relative', minHeight: 250 }}>
                                                    <DatePicker
                                                        key={currentMonthView.toISOString()}
                                                        inline
                                                        showDateSelect
                                                        selected={selectedDate}
                                                        filterDate={isDateAvailable}
                                                        onChange={(date: Date | null) => {
                                                            if (date) {
                                                                setSelectedDate(date);
                                                                setCurrentMonthView(date);
                                                            }
                                                        }}
                                                        onMonthChange={handleMonthChange}
                                                        openToDate={currentMonthView}
                                                    />

                                                    {isMonthLoading && (
                                                        <Box sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            right: 0,
                                                            bottom: 0,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                                            borderRadius: 'md',
                                                            zIndex: 1,
                                                        }}>
                                                            <CircularProgress size="md" />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ mb: '5px', width: '100%' }}>Days To Complete</Typography>
                                                {(() => {
                                                    const normalizedDaysToComplete = booking.days_to_complete || 1;
                                                    return (
                                                <Chip size="sm" variant="soft" color="neutral">
                                                    {normalizedDaysToComplete} {normalizedDaysToComplete === 1 ? 'day' : 'days'}
                                                </Chip>
                                                    );
                                                })()}
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                        </TabPanel>
                    </Tabs>
                </DrawerWithStickyFooter>
            </Drawer>
        </>
    );
}
